import { useState, useEffect, useMemo, useCallback } from 'react';
import { BannerData } from '../types';
import { InfoBannerContext } from './info-banner-context';
import { useGetInfoBanner } from '../../../hooks/query/content-api/use-banner';
import { getDismissedBannerId, setDismissedBannerId } from '../consts';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase';
import { useAuthContext } from '../../../auth';

//Mark the props of the component as read-only.
interface InfoBannerProviderProps {
  children: React.ReactNode;
}
export function InfoBannerProvider({ children }: Readonly<InfoBannerProviderProps>) {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [bannerHidden, setBannerHidden] = useState(false);
  const [bannerReady, setBannerReady] = useState(false);
  const { userId } = useAuthContext();

  const {
    data: bannerData,
    isLoading: isBannerLoading,
    isError: isBannerError,
  } = useGetInfoBanner({
    refetchInterval: 300_000, // 5 minutes
    refetchOnWindowFocus: true,
});

  useEffect(() => {
    if (isBannerLoading) {
      setBanner(null);
      setBannerReady(false);
      return;
    }

    if (isBannerError) {
        console.log("[ERROR] InfoBannerProvider: An error occured when fetching banner information from the content API")
        setBannerReady(true);
        return;
    }

    const banners = bannerData?.body?.banners;
    const firstBanner = banners?.[0];

    if (firstBanner) {
      setBanner({
        bannerHeading: firstBanner.bannerHeading,
        bannerBody: firstBanner.bannerBody,
        button: {
            buttonText: firstBanner?.button?.buttonText,
            buttonDestination: firstBanner?.button?.buttonDestination
        },
        activeFrom: firstBanner.activeFrom,
        expiresAt: firstBanner.expiresAt
      });
      // Respect a previous dismissal of this specific banner and also check if the banner has expired.
      setBannerHidden(getDismissedBannerId() === firstBanner.activeFrom || isBannerExpired(firstBanner.expiresAt));
    } else {
      setBanner(null);
    }

    // Mark resolved so the layout can render without flashing a stale banner.
    setBannerReady(true);
  }, [isBannerError, bannerData, isBannerLoading]);

  const hideBanner = useCallback(() => {
    setBannerHidden(true);
    if (banner?.activeFrom) {
      setDismissedBannerId(banner.activeFrom);
    }
    logEvent(analytics, 'banner_info_dismissed',{
      banner_url: banner?.button?.buttonDestination,
      banner_heading: banner?.bannerHeading,
      custom_user_id: userId,
    });
  }, [banner?.activeFrom, userId]);

  const isBannerExpired = (expiresAt: string) => {
    const currentDate = new Date();
    const expiryDate = new Date(expiresAt);
    return currentDate > expiryDate;
  };

  const contextValue = useMemo(
    () => ({
      bannerData: banner,
      bannerHidden: bannerHidden,
      bannerReady: bannerReady,
      setBannerHidden: hideBanner
    }),
    [banner, bannerHidden, bannerReady, hideBanner]
  );

  return (
    <InfoBannerContext.Provider value={contextValue}>
      {children}
    </InfoBannerContext.Provider>
  );
}
